import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  loadGroupData, BarCompareConstNat, WardGroupStaticMapWithBase,
  getTopVI, fetchLatestElection,
} from "./utils/metricsClient";
import mccainGroups from "./utils/polAggregators/mccain";
import { fetchClientDataset, formatClientMetric } from "./utils/clientData";
import { readableOn } from "./utils/segmentConfig";

/* ─── Design tokens (shared with ConstituencyReportView) ───────── */
const INK        = "#0f172a";
const INK2       = "#64748b";
const INK3       = "#94a3b8";
const BORDER     = "#e2e8f0";
const STACK_TEAL = "#0abfbc";
const STACK_DARK = "#2c2c2a";
const MCCAIN_RED = "#c8102e";

const PARTY_COLORS = {
  "Conservative": "#0087DC", "Labour": "#D50000",
  "Liberal Democrat": "#FDBB30", "Liberal Democrats": "#FDBB30",
  "Reform UK": "#12B6CF", "Reform": "#12B6CF",
  "Green": "#008066", "SNP": "#FFF95D", "Plaid Cymru": "#3F8428", "Other": "#64748b",
};
const partyCol = (label) => PARTY_COLORS[label] || STACK_TEAL;

/* ─── Micro-components ───────────────────────────────────────── */
const SL = ({ children, color = STACK_TEAL }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "4px", flexShrink: 0 }}>
    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: STACK_DARK, whiteSpace: "nowrap" }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "0.5px", bgcolor: BORDER }} />
  </Box>
);

const SlimPill = ({ label, party, share, majority }) => {
  if (!party) return null;
  const col = partyCol(party);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        bgcolor: `${col}20`,
        border: `0.5px solid ${col}`,
        borderRadius: "10px",
        px: "8px",
        py: "4px",
        flexShrink: 0,
        minWidth: 120,
        justifyContent: "flex-start",
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: col, flexShrink: 0 }} />
      <Box>
        <Typography sx={{ fontSize: 8, fontWeight: 600, color: INK3, lineHeight: 1, mb: "1px" }}>{label}</Typography>
        <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: INK, lineHeight: 1, whiteSpace: "nowrap" }}>
          {party}
          {share != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · {Number(share).toFixed(1)}%</Box>}
          {majority != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · Maj {Number(majority).toLocaleString()}</Box>}
        </Typography>
      </Box>
    </Box>
  );
};

/* FS.6 is categorical, not likert: "I don't think the weather has changed" sits
   outside the harder/easier scale, so the group ships no net. "Made farming
   harder" is the two harder points summed; no impact, easier, not changed and
   don't know stay in the base but out of the numerator. */
const HARDER_KEYS = [
  "McCain_July26_weather_considerably_harder",
  "McCain_July26_weather_somewhat_harder",
];

/* The two halves of that total, drawn as separate arcs so the intensity split
   is visible. Colours are the group's own from mccain.js: deep red for
   considerably, soft red for somewhat. */
const HARDER_SPLIT = [
  { key: "McCain_July26_weather_considerably_harder", label: "Considerably harder", color: "#C62828" },
  { key: "McCain_July26_weather_somewhat_harder",     label: "Somewhat harder",     color: "#EF5350" },
];

/* BFG.1. The headline is the unfair side — the two negative points summed —
   because that is the side the policy questions respond to. Fair, don't know
   and the rest stay in the base. */
const UNFAIR_KEYS = [
  "McCain_July26_farmers_paid_mostly_unfair",
  "McCain_July26_farmers_paid_almost_always_unfair",
];
/** Sum a set of response keys, or null when none of them carry a figure. */
const sumKeys = (rows, keys, field) => {
  const vals = (rows || [])
    .filter(d => keys.includes(d.key))
    .map(d => Number(d[field]))
    .filter(Number.isFinite);
  return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
};

const sumHarder = (rows, field) => sumKeys(rows, HARDER_KEYS, field);
const sumUnfair = (rows, field) => sumKeys(rows, UNFAIR_KEYS, field);

/* Red / amber / green, applied in slider order rather than by position, so the
   colours stay attached to their meaning if the data ever returns in a
   different order. The same three make up the FS.10 band below. */
const RAG_RED    = "#cc0000";
const RAG_AMBER  = "#f1c232";
const RAG_GREEN  = "#6aa84f";

/* FS.4's own labels carry the slider ranges and run long; these are the tile
   versions. Keyed off the same ids so a label change upstream is visible here. */
const EFFICIENCY_TILES = {
  "McCain_July26_FS4_efficiency_v_sustainability_maximise_efficiency_now": {
    label: "Efficiency / profitability now (0-3)", color: RAG_RED,
  },
  "McCain_July26_FS4_efficiency_v_sustainability_neither": {
    label: "Balance (4-6)", color: RAG_AMBER,
  },
  "McCain_July26_FS4_efficiency_v_sustainability_preserve_for_future": {
    label: "Preserving farming resources for future generations (7-10)", color: RAG_GREEN,
  },
};

const LOCAL_FOOD_HIGHER = "McCain_July26_local_food_higher";

/* FS.10 as a proportional band. The question has five options; the band shows
   the three substantive ones ordered production → both → environment so the
   ramp reads as a spectrum. Shares are raw, not rebased, so the band stops
   short of the full width by whatever "not be paid at all" and "don't know"
   account for, and that remainder is named underneath. Colours are a deliberate
   sequential ramp rather than the group's own categorical palette, which would
   not read as a spectrum. */
const FS10_SEGMENTS = [
  {
    key: "McCain_July26_support_approach_how_much_food",
    label: "Mainly based on production",
    bg: RAG_RED,
  },
  {
    key: "McCain_July26_support_approach_both",
    label: "Based on both production and environmental protection",
    bg: RAG_AMBER,
  },
  {
    key: "McCain_July26_support_approach_look_after_environment",
    label: "Mainly based on environmental protection",
    bg: RAG_GREEN,
  },
// Text colour is computed per segment rather than listed: amber needs dark
// text where red and green need white, and hard-coding that invites a
// contrast bug the moment a colour changes.
].map(s => ({ ...s, fg: readableOn(s.bg) }));
const FS10_EXCLUDED = [
  { key: "McCain_July26_support_approach_not_paid", label: "not be paid at all" },
  { key: "McCain_July26_support_approach_dk", label: "don't know" },
];

const pct = (v) => Math.max(0, Math.min(100, Number(v) || 0));

/* The external extract carries a per-country deprivation rank but not the
   country itself, so it comes off the ONS code prefix.
   "English" / "Scottish" / "Welsh" rather than the country name, which reads
   better attached to a noun: "543 English constituencies". */
const demonymFromCode = (code) => {
  const c = String(code || "");
  if (c.startsWith("E")) return "English";
  if (c.startsWith("S")) return "Scottish";
  if (c.startsWith("W")) return "Welsh";
  return null;
};

/** Possessive form. Names already ending in s take a bare apostrophe, so
 *  "St Ives" reads "St Ives'" rather than "St Ives's". */
const possessive = (name) => {
  const n = String(name || "").trim();
  if (!n) return "";
  return /s$/i.test(n) ? `${n}'` : `${n}'s`;
};

const ordinal = (n) => {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return "—";
  const mod100 = v % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${v}th`;
  return `${v}${["th", "st", "nd", "rd"][v % 10] || "th"}`;
};

const ESSENTIAL_KEY = "McCain_July26_farming_importance_essential";

/**
 * Client logo, following the same convention as Sidebar.js: try
 * /logos/<slug>.svg then .png, and render nothing if neither exists. Missing is
 * the normal case until the file is added, and a broken-image icon in an
 * exported PDF would be worse than no logo at all.
 */
const ClientLogo = ({ slug = "mccain", height = 15 }) => {
  const candidates = useMemo(() => [
    `${process.env.PUBLIC_URL}/logos/${slug}.svg`,
    `${process.env.PUBLIC_URL}/logos/${slug}.png`,
  ], [slug]);
  const [idx, setIdx] = useState(0);

  if (idx >= candidates.length) return null;
  return (
    <Box
      component="img"
      src={candidates[idx]}
      alt=""
      onError={() => setIdx(i => i + 1)}
      sx={{ height, width: "auto", flexShrink: 0, display: "block" }}
    />
  );
};


/** Knife-and-fork mark for the food-security rows. Inline SVG rather than an
 *  emoji, which rasterises inconsistently through html2canvas. */
const FoodMark = () => (
  <Box sx={{
    width: 26, height: 26, borderRadius: "50%", bgcolor: "#d8f3f5",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  }}>
    <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true">
      <g stroke="#0f7d86" strokeWidth="1.8" strokeLinecap="round" fill="none">
        <path d="M7 2v8" /><path d="M4.5 2v4a2.5 2.5 0 0 0 5 0V2" /><path d="M7 10v12" />
        <path d="M17 2c-1.6 1.4-2.2 3.4-2.2 5.5 0 1.6.7 2.8 2.2 3.2V22" />
      </g>
    </svg>
  </Box>
);

/** One food-security line: mark, headline figure, description. */
const FoodRow = ({ value, children }) => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: "11px",
    border: `1px solid ${BORDER}`, borderRadius: "8px",
    bgcolor: "#f8fafc", px: "11px", py: "9px",
  }}>
    <FoodMark />
    <Box sx={{ width: "0.5px", alignSelf: "stretch", bgcolor: BORDER, flexShrink: 0 }} />
    <Typography sx={{
      fontSize: 23, fontWeight: 800, color: INK, lineHeight: 1,
      flexShrink: 0, minWidth: 62,
    }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 11, color: INK2, lineHeight: 1.3 }}>
      {children}
    </Typography>
  </Box>
);

/* ── FS.8 policy support table ──────────────────────────────────
   farming_policies is a likert_grid whose `keys` are the flat union of every
   sub-variable's six points, so one fetch covers all eleven policies. Support
   for a policy is its two support points summed. */
const FS8_SUBS = mccainGroups.farming_policies?.subVariables || [];
const FS8_STEM = "To what extent do you support or oppose each of the following suggested policies?";

/** Full policy wording, taken from the sub-question's own fulltext rather than
 *  restated here, minus the stem the eleven of them share. */
const policyWording = (sub) => {
  const ft = mccainGroups[sub]?.fulltext || "";
  const i = ft.indexOf(FS8_STEM);
  const tail = (i >= 0 ? ft.slice(i + FS8_STEM.length) : ft).trim();
  return tail || mccainGroups.farming_policies?.subLabels?.[sub] || sub;
};

const supportKeys = (sub) => [
  `McCain_July26_${sub}_strong_support`,
  `McCain_July26_${sub}_somewhat_support`,
];

/** Sum a policy's two support points from the flat grid rows. */
const policySupport = (rows, sub, field) => {
  const wanted = supportKeys(sub);
  const vals = (rows || [])
    .filter(d => wanted.includes(d.key))
    .map(d => Number(d[field]))
    .filter(Number.isFinite);
  return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
};

/**
 * Blend a hex toward white (t > 0) or black (t < 0), 0..1.
 * Used to derive a darker/lighter pair from one party colour, so a two-part
 * donut can read as one party's answer split by intensity rather than as two
 * unrelated categories.
 */
const shade = (hex, t) => {
  const h = String(hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  if (full.length !== 6) return hex;
  const to = t >= 0 ? 255 : 0;
  const k = Math.min(1, Math.abs(t));
  const ch = (i) => {
    const c = parseInt(full.slice(i, i + 2), 16);
    if (!Number.isFinite(c)) return "00";
    return Math.round(c + (to - c) * k).toString(16).padStart(2, "0");
  };
  return `#${ch(0)}${ch(2)}${ch(4)}`;
};

/** "#D50000" → "213, 0, 0", so a party hex can be used inside rgba(). */
const hexToRgb = (hex) => {
  const h = String(hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  if (full.length !== 6) return "100, 116, 139";
  const n = parseInt(full, 16);
  if (!Number.isFinite(n)) return "100, 116, 139";
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

/** Heat shade for a table cell: `t` is the value's position in the column's
 *  own range, so the ramp uses the full contrast available rather than
 *  compressing eleven similar percentages into one flat tone. */
const heatCell = (t, rgb) => ({
  bg: `rgba(${rgb}, ${(0.16 + 0.74 * t).toFixed(3)})`,
  fg: t > 0.5 ? "#ffffff" : INK,
});

/**
 * Stat tile: one headline figure, its comparison in brackets, the gap as a
 * signed chip, and a track whose tick marks where the comparison sits.
 */
const StatTile = ({ title, value, compare, color }) => {
  // Number(null) is 0, which is finite — so an explicitly empty tile would
  // print "0%" rather than "—" without the null guard.
  const has  = value   != null && Number.isFinite(Number(value));
  const hasC = compare != null && Number.isFinite(Number(compare));
  const delta = has && hasC ? Number(value) - Number(compare) : null;
  // Same 0.1pp floor as every other comparison in this report.
  const moved = delta != null && Math.abs(delta) >= 0.1;
  const tone   = !moved ? INK2 : delta > 0 ? "#15803d" : "#b91c1c";
  const toneBg = !moved ? "#f1f5f9" : delta > 0 ? "#dcfce7" : "#fee2e2";

  return (
    <Box sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: "8px",
      p: "10px 11px",
      bgcolor: "#fff",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      minWidth: 0,
    }}>
      <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK, textAlign: "center", lineHeight: 1.25 }}>
        {title}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "5px", flexWrap: "nowrap" }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: INK, lineHeight: 1 }}>
          {has ? `${Number(value).toFixed(0)}%` : "—"}
        </Typography>
        <Typography sx={{ fontSize: 10, color: INK3 }}>
          {hasC ? `(${Number(compare).toFixed(0)}%)` : ""}
        </Typography>
        {moved && (
          <Box sx={{ bgcolor: toneBg, borderRadius: "4px", px: "5px", py: "1px", flexShrink: 0 }}>
            <Typography sx={{ fontSize: 8.5, fontWeight: 700, color: tone, whiteSpace: "nowrap" }}>
              {delta > 0 ? "+" : "-"}{Math.abs(delta).toFixed(1)} pp
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ position: "relative", height: 6, borderRadius: "3px", bgcolor: "#eef2f7" }}>
        {has && (
          <Box sx={{
            position: "absolute", left: 0, top: 0, bottom: 0,
            width: `${pct(value)}%`, bgcolor: color, borderRadius: "3px", opacity: 0.85,
          }} />
        )}
        {hasC && (
          <Box sx={{
            position: "absolute", left: `${pct(compare)}%`, top: "-2px", bottom: "-2px",
            width: "2px", bgcolor: INK, borderRadius: "1px",
          }} />
        )}
      </Box>
    </Box>
  );
};

/**
 * One A4 tile.
 *
 * The page is the unit, not the document. Each tile is clipped to exactly
 * 794x1123 (A4 at 96dpi) with overflow hidden, so content can never spill
 * between pages: anything that overflows is a layout bug you can see in the
 * preview, not a silent page break discovered in the exported PDF.
 *
 * `id` is what the exporter addresses. Pages are suffixed -p1, -p2, … and
 * generatePdfFor walks them in order.
 */
const Page = ({ id, height, rows, children }) => (
  <Box
    id={id}
    sx={{
      backgroundColor: "white",
      color: "black",
      width: "794px",
      height,
      overflow: "hidden",
      p: "12px 18px",
      border: "1px solid #ccc",
      boxSizing: "border-box",
      display: "grid",
      gridTemplateRows: rows,
      gridTemplateColumns: "1fr",
      gap: "6px",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

/**
 * Single-figure doughnut with a comparison ring.
 *
 * Thick outer arc is the headline population, thin inner arc is the comparison.
 * That is the same relationship the bar charts use (thick = subject, thin =
 * benchmark), so both panels read with one grammar. Inline SVG, which the
 * html2canvas export path already handles elsewhere in the report suite.
 *
 * `segments` optionally breaks the headline arc into consecutive coloured
 * pieces — [{ value, color }] — so a combined figure can show what it is made
 * of. The centre still reads the total. Butt caps are used in that mode: round
 * caps overlap their neighbour and blur the very boundary the split exists to
 * show.
 */
const DonutStat = ({ value, compareValue, color, size = 132, segments = null }) => {
  // Everything scales off `size`. Fixed pixel values worked at 132 but not at
  // 96, where a 28px centre label overran the inner comparison ring.
  const SW  = size * 0.12;   // headline ring thickness
  const SWC = size * 0.05;   // comparison ring thickness
  const GAP = size * 0.04;
  const FONT = size * 0.2;
  const cx = size / 2;
  const r  = (size - SW) / 2;
  const rc = r - SW / 2 - GAP - SWC / 2;
  const circ  = 2 * Math.PI * r;
  const circC = 2 * Math.PI * rc;

  const clamp = (v) => Math.max(0, Math.min(100, Number(v)));
  // Null-guarded: Number(null) is 0 and finite, which would draw an empty
  // donut as a real zero rather than as "no data".
  const v = value != null && Number.isFinite(Number(value)) ? clamp(value) : null;
  const hasCompare = compareValue != null && Number.isFinite(Number(compareValue));
  const cv = hasCompare ? clamp(compareValue) : 0;

  // Usable segments with their running start position, so each arc knows where
  // the previous one finished. Falls back to the single-colour ring.
  const drawn = [];
  if (Array.isArray(segments)) {
    let start = 0;
    for (const s of segments) {
      const sv = Number(s?.value);
      if (!Number.isFinite(sv) || sv <= 0) continue;
      drawn.push({ value: sv, color: s.color, start });
      start += sv;
    }
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
      {/* Arcs start at twelve o'clock rather than three. */}
      <g transform={`rotate(-90 ${cx} ${cx})`}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="#eef2f7" strokeWidth={SW} />
        {v != null && (
          drawn.length ? (
            // Each piece starts where the previous one ended, via a negative
            // dash offset.
            drawn.map((s, i) => (
              <circle
                key={i}
                cx={cx} cy={cx} r={r}
                fill="none" stroke={s.color} strokeWidth={SW}
                strokeDasharray={`${(s.value / 100) * circ} ${circ}`}
                strokeDashoffset={`${-(s.start / 100) * circ}`}
                strokeLinecap="butt"
              />
            ))
          ) : (
            <circle
              cx={cx} cy={cx} r={r}
              fill="none" stroke={color} strokeWidth={SW}
              strokeDasharray={`${(v / 100) * circ} ${circ}`}
              strokeLinecap="round"
            />
          )
        )}
        {hasCompare && (
          <>
            <circle cx={cx} cy={cx} r={rc} fill="none" stroke="#f1f5f9" strokeWidth={SWC} />
            <circle
              cx={cx} cy={cx} r={rc}
              fill="none" stroke={INK3} strokeWidth={SWC}
              strokeDasharray={`${(cv / 100) * circC} ${circC}`}
              strokeLinecap="round"
            />
          </>
        )}
      </g>
      <text
        x={cx} y={cx}
        textAnchor="middle" dominantBaseline="central"
        style={{ fontSize: FONT, fontWeight: 800, fill: INK }}
      >
        {v != null ? `${v.toFixed(0)}%` : "—"}
      </text>
    </svg>
  );
};

/**
 * Header. Same construction as the political report's InlineHeader (logo strip,
 * seat name, the three result pills), with two McCain-specific additions: the
 * report is titled for the farming dataset, and a badge names the voting-intention
 * cut so a Labour PDF can never be mistaken for a Reform one once it is off screen.
 */
const InlineHeader = ({ constName, areaCode, topVI, vi }) => {
  const [latestElection, setLatestElection] = useState(null);
  const [winner2019, setWinner2019]         = useState(null);

  useEffect(() => {
    if (!areaCode) return;
    let cancelled = false;
    fetchLatestElection(areaCode).then(d => { if (!cancelled) setLatestElection(d); }).catch(() => {});
    const CSV_URL = process.env.PUBLIC_URL + "/static/data/Notional2019Winners_Newpcon.csv";
    fetch(CSV_URL).then(r => r.text()).then(text => {
      if (cancelled) return;
      const lines = text.trim().split("\n");
      if (lines.length < 2) return;
      const header = lines[0].replace(/^﻿/, "").split(",");
      for (const line of lines.slice(1)) {
        const cols = line.split(",");
        const row = Object.fromEntries(header.map((k, i) => [k.trim(), cols[i]?.trim()]));
        if (row.new_pcon === areaCode) {
          const shares = {
            Conservative: Number(row.con19), Labour: Number(row.lab19),
            "Liberal Democrat": Number(row.ld19), Green: Number(row.green19),
            Reform: Number(row.brx19), "Plaid Cymru": Number(row.pc19),
            SNP: Number(row.snp19), Other: Number(row.other19),
          };
          // The CSV spells two winners differently from the keys above
          // ("LibDem", "PlaidCymru"), so `shares[row.winner]` was undefined and
          // the pill printed 0.0% — and the wrong colour — in the 11 seats
          // those two won in 2019. Map the label onto the canonical name once,
          // before it is used for either.
          const party2019 = { LibDem: "Liberal Democrat", PlaidCymru: "Plaid Cymru" }[row.winner] || row.winner;
          const party = party2019 || Object.entries(shares).reduce(
            (a, b) => (b[1] > (shares[a] || 0) ? b[0] : a), "Other"
          );
          setWinner2019({ party, share: (shares[party] || 0) * 100 });
          break;
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [areaCode]);

  const isParty = !!vi && vi.code !== "all";
  const accent = isParty
    ? vi.color
    : (latestElection?.party ? partyCol(latestElection.party) : STACK_TEAL);

  return (
    <Box sx={{ pb: "5px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
        <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 13, opacity: 0.6 }} />
        <ClientLogo slug="mccain" height={15} />
        <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
          McCain Report
        </Typography>
        {/* The cut lives up here rather than beside the seat name, which lets
            the name have the full width instead of being ellipsised by it. */}
        {isParty && (
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            bgcolor: accent,
            borderRadius: "11px",
            px: "9px",
            py: "3px",
            flexShrink: 0,
            boxShadow: `0 1px 3px ${accent}66`,
          }}>
            <Box sx={{
              width: 7, height: 7, borderRadius: "50%",
              bgcolor: readableOn(accent), opacity: 0.9, flexShrink: 0,
            }} />
            <Typography sx={{
              fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em",
              textTransform: "uppercase", color: readableOn(accent),
              whiteSpace: "nowrap", lineHeight: 1.4,
            }}>
              {vi.label} voters
            </Typography>
          </Box>
        )}
        <Typography sx={{ ml: "auto", fontSize: 8.5, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", alignItems: "center", columnGap: "8px", mb: "3px" }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {constName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <SlimPill label="VI Projection" party={topVI?.label} share={topVI?.constituency} />
          <SlimPill label="2024 Result"   party={latestElection?.party} share={latestElection?.share} majority={latestElection?.majority} />
          <SlimPill label="2019 Result"   party={winner2019?.party} share={winner2019?.share} />
        </Box>
      </Box>
      {/* On a party cut this runs the full width at double weight, so the page
          reads as that party's edition even when the badge is out of view. */}
      <Box sx={{
        height: isParty ? 5 : 3,
        width: isParty ? "100%" : 180,
        borderRadius: 4,
        background: `linear-gradient(90deg, ${accent}, ${accent}${isParty ? "88" : "55"})`,
      }} />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════
   McCAIN REPORT VIEW

   One report per voting-intention cut: `vi` is {code, label, color}, where
   code === "all" means no filter. Party cuts post `filters: { segment: [code] }`,
   which core/views.py resolves to the mccain schema's `voting_intention` column
   (SCHEMA_SEGMENT_COL) — the same generic filter key every schema uses.

   Grid rows inside the fixed A4 box:
     "header"   - auto  (logo strip, seat name, VI badge, result pills)
     "maps"     - 1fr   (past-vote ward map | VI bars | VI ward map)
     "external" - auto  (reserved block, fed from an external source)
     "charts"   - 1.5fr (bottom row; farming problems on the left)
═══════════════════════════════════════════════════════════════ */

const McCainReportView = ({
  constName,
  areaCode,
  vi = null,                 // {code, label, color}; code "all" => unfiltered
  height = "1123px",
  idPrefix,                  // "report-content-<itemId>"; pages become -p1, -p2
}) => {
  const isPartyCut = !!vi && vi.code !== "all";
  const viCode = isPartyCut ? vi.code : null;

  const [charts, setCharts] = useState({
    vi: [], problems: [], problemsParty: [], climate: [], climateParty: [],
    efficiency: [], localFood: [], supportApproach: [], supportApproachAll: [],
    policies: [], policiesParty: [], importance: [], importanceAll: [],
    localFoodAll: [], efficiencyAll: [], paid: [], paidParty: [], loading: true,
  });
  const topVI = getTopVI(charts.vi);
  const prevKey = useRef(null);

  // External (non-survey) constituency data, served by /api/client_data/.
  // Independent of the VI cut: it describes the seat, not the respondents.
  const [clientData, setClientData] = useState(null);
  useEffect(() => {
    if (!areaCode) return;
    let cancelled = false;
    fetchClientDataset({ datasetId: "mccain_farming", areaCode })
      .then(d => { if (!cancelled) setClientData(d); });
    return () => { cancelled = true; };
  }, [areaCode]);

  useEffect(() => {
    if (!areaCode) return;
    // The party cut is part of the identity of this report, so a Labour and a
    // Reform card for the same seat must each trigger their own load.
    const key = `${areaCode}|${viCode ?? "all"}`;
    if (key === prevKey.current) return;
    prevKey.current = key;
    setCharts({
      vi: [], problems: [], problemsParty: [], climate: [], climateParty: [],
      efficiency: [], localFood: [], supportApproach: [], supportApproachAll: [],
    policies: [], policiesParty: [], importance: [], importanceAll: [],
    localFoodAll: [], efficiencyAll: [], paid: [], paidParty: [], loading: true,
    });

    (async () => {
      const partyOnly = (groupId) =>
        viCode
          ? loadGroupData({ groupId, areaCode, level: "constituency", filters: { segment: [viCode] } })
          : Promise.resolve([]);
      // Page 2 compares constituency against national within the report's own
      // population, so one filtered call covers both figures: loadGroupData
      // applies `filters` to the constituency and the national query alike.
      const inScope = (groupId) =>
        loadGroupData({
          groupId,
          areaCode,
          level: "constituency",
          ...(viCode ? { filters: { segment: [viCode] } } : {}),
        });
      try {
        const [
          viRows, problems, problemsParty, climate, climateParty,
          efficiency, localFood, supportApproach, supportApproachAll,
          policies, policiesParty, importance, localFoodAll, efficiencyAll,
          importanceAll, paid, paidParty,
        ] = await Promise.all([
          // Vote intention comes from the tracking schema and is never filtered:
          // it is the seat's political context, identical across every cut.
          loadGroupData({ groupId: "vote_intention", areaCode, level: "constituency" }),
          loadGroupData({ groupId: "farming_problems", areaCode, level: "constituency" }),
          partyOnly("farming_problems"),
          loadGroupData({ groupId: "climate_change_farming", areaCode, level: "constituency" }),
          partyOnly("climate_change_farming"),
          inScope("efficiency_v_sustainability"),
          inScope("local_food_production"),
          inScope("support_farmers_approach"),
          // Unfiltered companion: the seat as a whole, shown under the band's
          // party figure.
          loadGroupData({ groupId: "support_farmers_approach", areaCode, level: "constituency" }),
          loadGroupData({ groupId: "farming_policies", areaCode, level: "constituency" }),
          partyOnly("farming_policies"),
          // Follows the report's cut: on a party edition the tile is that
          // party's voters in this seat. Its unfiltered companion is fetched
          // below, both as the tile's benchmark and for the sentence above it,
          // which is about the seat's residents whatever the cut.
          inScope("farming_importance"),
          // Unfiltered companion: the seat as a whole, which is what a party
          // cut is compared against.
          loadGroupData({ groupId: "local_food_production", areaCode, level: "constituency" }),
          loadGroupData({ groupId: "efficiency_v_sustainability", areaCode, level: "constituency" }),
          loadGroupData({ groupId: "farming_importance", areaCode, level: "constituency" }),
          // BFG.1, modelled as of the latest sync. Same unfiltered + party pair
          // as the weather figure below it.
          loadGroupData({ groupId: "farmers_paid_fair", areaCode, level: "constituency" }),
          partyOnly("farmers_paid_fair"),
        ]);
        setCharts({
          vi: viRows, problems, problemsParty, climate, climateParty,
          efficiency, localFood, supportApproach, supportApproachAll,
          policies, policiesParty, importance, importanceAll,
          localFoodAll, efficiencyAll, paid, paidParty,
          loading: false,
        });
      } catch {
        setCharts(s => ({ ...s, loading: false }));
      }
    })();
  }, [areaCode, viCode]);

  /* ── Farming problems: what the two bars mean ──────────────────
     BarCompareConstNat draws a thick bar from `constituency` and a thin one
     from `national`; neither carries a text label, so the caption below the
     chart names them.

     All voters : thick = this seat, thin = GB.
     Party cut  : thick = this seat (all voters), thin = this seat's voters for
                  the selected party, so the thin bar reads as that party's
                  divergence from the seat as a whole.                        */
  const problemsChart = useMemo(() => {
    const base = charts.problems || [];
    if (!isPartyCut) return base;
    const partyByKey = new Map((charts.problemsParty || []).map(d => [d.key, d]));
    return base.map(d => ({
      ...d,
      national: Number(partyByKey.get(d.key)?.constituency ?? NaN),
    }));
  }, [charts.problems, charts.problemsParty, isPartyCut]);

  const secondBarLabel = isPartyCut ? `${vi.label} voters here` : "National";

  /* Hold the longest bar at 75% of its track rather than letting it run the
     full width. BarCompareConstNat treats scaleMax as a floor for the axis
     maximum, so dividing the largest plotted value by 0.75 sets the axis just
     wide enough to leave a quarter of the track clear. Computed from the data
     rather than fixed, because these are shares of selections spread over
     thirteen options and never approach a round ceiling. */
  const problemsScaleMax = useMemo(() => {
    const vals = problemsChart
      .flatMap(d => [d.constituency, d.national])
      .filter(v => Number.isFinite(Number(v)))
      .map(Number);
    const max = vals.length ? Math.max(...vals) : 0;
    return max > 0 ? max / 0.75 : undefined;
  }, [problemsChart]);

  // On a party cut the comparison bar is a different population, not the same
  // one nationally, so it takes the party's colour at close to full strength.
  // The national comparison keeps the faint default.
  const partyBarProps = isPartyCut
    ? { natColor: vi.color, natOpacity: 0.9, natThickness: 6, natDeltaHighlight: true }
    : {};

  /* ── Weather made farming harder ───────────────────────────────
     Headline and comparison follow the same pairing as the bar chart:
       All voters : this seat vs GB.
       Party cut  : that party in this seat vs the seat as a whole.           */
  const climateSeat  = useMemo(() => sumHarder(charts.climate, "constituency"), [charts.climate]);
  const climateNat   = useMemo(() => sumHarder(charts.climate, "national"), [charts.climate]);
  const climateParty = useMemo(() => sumHarder(charts.climateParty, "constituency"), [charts.climateParty]);

  /* The same population the donut's total describes, split into its two points.
     On a party edition the two arcs are a dark/light pair derived from that
     party's own colour, so the ring still reads as that party; the All voters
     version keeps the group's reds. Index 0 is "considerably", which takes the
     darker shade. */
  const climateSegments = useMemo(() => {
    const rows = isPartyCut ? charts.climateParty : charts.climate;
    const base = isPartyCut ? vi.color : null;
    return HARDER_SPLIT.map((s, i) => ({
      ...s,
      color: base ? shade(base, i === 0 ? -0.3 : 0.45) : s.color,
      value: Number((rows || []).find(d => d.key === s.key)?.constituency),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charts.climate, charts.climateParty, isPartyCut, vi?.color]);

  const climateValue   = isPartyCut ? climateParty : climateSeat;
  const climateCompare = isPartyCut ? climateSeat : climateNat;
  const climateCompareLabel = isPartyCut ? `${constName} (all voters)` : "National";
  const climateDelta =
    Number.isFinite(Number(climateValue)) && Number.isFinite(Number(climateCompare))
      ? Number(climateValue) - Number(climateCompare)
      : null;
  // Same 0.1pp floor the bar chart uses, so a gap that rounds away at one
  // decimal place is not given an arrow the printed numbers contradict.
  const climateMoved = climateDelta != null && Math.abs(climateDelta) >= 0.1;
  const climateTone = !climateMoved ? INK2 : climateDelta > 0 ? "#15803d" : "#b91c1c";

  /* ── Farmers paid unfairly (BFG.1) ─────────────────────────────
     Same pairing as the weather figure: a party cut is that party's voters in
     this seat against the seat as a whole, the unfiltered version against GB. */
  const paidSeat  = useMemo(() => sumUnfair(charts.paid, "constituency"), [charts.paid]);
  const paidNat   = useMemo(() => sumUnfair(charts.paid, "national"), [charts.paid]);
  const paidPartyV = useMemo(() => sumUnfair(charts.paidParty, "constituency"), [charts.paidParty]);

  const paidValue   = isPartyCut ? paidPartyV : paidSeat;
  const paidCompare = isPartyCut ? paidSeat : paidNat;
  const paidCompareLabel = isPartyCut ? `${constName} (all voters)` : "National";
  const paidDelta =
    Number.isFinite(Number(paidValue)) && Number.isFinite(Number(paidCompare))
      ? Number(paidValue) - Number(paidCompare)
      : null;
  const paidMoved = paidDelta != null && Math.abs(paidDelta) >= 0.1;
  const paidTone = !paidMoved ? INK2 : paidDelta > 0 ? "#15803d" : "#b91c1c";

  /* ── Farming in the constituency ───────────────────────────────
     The land and food-security figures here are external constituency data and
     are the same on every edition. The importance tile is not: it follows the
     report's cut, benchmarked against the seat as a whole — the same
     party-vs-seat pairing page 1 uses — because the unfiltered seat figure is
     dominated by the half of the raked weight that says it will not vote, and
     sat below every party's own voters as a result.

     `allSeat`/`allNational` are the seat as a whole against GB. The sentence
     above the tiles is about the seat's residents, so it reads those and stays
     identical across the three editions. */
  const essential = useMemo(() => {
    const row = (charts.importance || []).find(d => d.key === ESSENTIAL_KEY);
    if (!row) return null;
    const all = (charts.importanceAll || []).find(d => d.key === ESSENTIAL_KEY);
    return {
      seat: Number(row.constituency),
      compare: isPartyCut ? Number(all?.constituency) : Number(row.national),
      allSeat: Number(all?.constituency),
      allNational: Number(all?.national),
    };
  }, [charts.importance, charts.importanceAll, isPartyCut]);

  const landFarmed = clientData?.area
    ? { seat: Number(clientData.area.farm_core_pct),
        national: Number(clientData.national?.farm_core_pct) }
    : null;

  /* Farming's share of local employment, derived from the two counts rather
     than read off a stored percentage. Doing the division here means the GB
     figure is the true ratio of the sums (12.8%) rather than an average of 632
     seat percentages (14.4%) — a 1.6pp difference that every tile on this row
     compares against. */
  const share = (num, den) => {
    const n = Number(num), d = Number(den);
    return Number.isFinite(n) && Number.isFinite(d) && d > 0 ? (n / d) * 100 : NaN;
  };
  const farmingEmployed = clientData?.area
    ? {
        seat: share(clientData.area.farming_employed, clientData.area.total_employed),
        national: share(clientData.national?.farming_employed, clientData.national?.total_employed),
      }
    : null;

  // "More likely" is a relative comparison, not a difference in points: 96%
  // against 83% is 16% more likely, while the tile below it shows +13.3pp.
  // Both are correct and they are deliberately different quantities. The seat
  // as a whole against GB, to match the "residents" the sentence names.
  const essentialUplift =
    essential && Number.isFinite(essential.allSeat) && Number.isFinite(essential.allNational)
      && essential.allNational > 0
      ? ((essential.allSeat - essential.allNational) / essential.allNational) * 100
      : null;

  /* The sentence is built from both measures rather than assuming they agree.
     A seat can easily have more land farmed but fewer people working in it, so
     "more / and / but / fewer" are chosen from the actual comparison. */
  const above = (m) =>
    m && Number.isFinite(m.seat) && Number.isFinite(m.national) ? m.seat > m.national : null;

  const farmingIntro = useMemo(() => {
    const moreLand = above(landFarmed);
    const moreJobs = above(farmingEmployed);
    if (moreLand === null || moreJobs === null) return null;
    return {
      land: moreLand ? "more land farmed" : "less land farmed",
      jobs: moreJobs ? "more people working in farming" : "fewer people working in farming",
      joiner: moreLand === moreJobs ? "and" : "but",
      // Only lead with "Despite" when the seat is below on both counts.
      concessive: !moreLand && !moreJobs,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landFarmed?.seat, landFarmed?.national, farmingEmployed?.seat, farmingEmployed?.national]);

  /* ── Page 2 ────────────────────────────────────────────────────
     Both of these read constituency against national within the report's own
     population: on a Labour report the tiles are Labour voters in this seat
     against Labour voters nationally. That is a different pairing from page 1,
     where the party is compared against the seat as a whole. */
  /* Value is the report's own population in this seat; the bracketed figure and
     tick follow the same party-vs-seat pairing as page 1, so a party cut is
     compared against the constituency as a whole rather than against that party
     nationally. The unfiltered version still benchmarks against GB. */
  const efficiencyTiles = useMemo(() => {
    const seatAll = new Map(
      (charts.efficiencyAll || []).map(d => [d.key, Number(d.constituency)])
    );
    return (charts.efficiency || [])
      .filter(d => EFFICIENCY_TILES[d.key])
      .map(d => ({
        ...d,
        compare: isPartyCut ? seatAll.get(d.key) : Number(d.national),
      }));
  }, [charts.efficiency, charts.efficiencyAll, isPartyCut]);

  /* Headline is the report's own population in this seat. The benchmark follows
     the party-vs-seat pairing used on page 1: a party cut is compared against
     the constituency as a whole, and the unfiltered version against GB. */
  const localFoodHigher = useMemo(() => {
    const row = (charts.localFood || []).find(d => d.key === LOCAL_FOOD_HIGHER);
    if (!row) return null;
    const seatAll = Number(
      (charts.localFoodAll || []).find(d => d.key === LOCAL_FOOD_HIGHER)?.constituency
    );
    return {
      seat: Number(row.constituency),
      compare: isPartyCut ? seatAll : Number(row.national),
      compareLabel: isPartyCut ? constName : "Nationally",
    };
  }, [charts.localFood, charts.localFoodAll, isPartyCut, constName]);

  // Names the seat on party editions too. Without it "those planning to vote
  // Conservative" reads as a national figure rather than this constituency's.
  const audience = isPartyCut
    ? `those planning to vote ${vi.label} (in ${constName})`
    : `voters in ${constName}`;

  // FS.10 band: raw shares, not rebased. Widths are those same shares against a
  // full 100% track, so the band stops short by whatever the two options it
  // does not show account for, and every width matches its printed number.
  const fs10 = useMemo(() => {
    const rows = charts.supportApproach || [];
    const at = (k) => Number(rows.find(d => d.key === k)?.constituency);
    const vals = FS10_SEGMENTS.map(s => at(s.key));
    if (!vals.every(v => Number.isFinite(v))) return null;
    /* The subscript under each headline figure. A party edition compares that
       party against the seat as a whole, matching the pairing used everywhere
       else on a party cut; the unfiltered edition has no party to contrast with,
       so it compares the seat against GB instead. */
    const allRows = charts.supportApproachAll || [];
    const compareAt = (k) => isPartyCut
      ? Number(allRows.find(d => d.key === k)?.constituency)
      : Number(rows.find(d => d.key === k)?.national);
    // `share` is the raw figure and is what gets printed. `width` rebases the
    // three against each other so the band fills the full track — a layout
    // measure only, deliberately not the number on the segment.
    const shown = vals.reduce((a, b) => a + b, 0);
    return {
      segments: FS10_SEGMENTS.map((s, i) => ({
        ...s,
        share: vals[i],
        width: shown > 0 ? (vals[i] / shown) * 100 : 100 / vals.length,
        compare: compareAt(s.key),
      })),
      excluded: FS10_EXCLUDED
        .map(e => ({ ...e, value: at(e.key) }))
        .filter(e => Number.isFinite(e.value)),
    };
  }, [charts.supportApproach, charts.supportApproachAll, isPartyCut]);

  /* FS.8: one row per policy. A party edition ranks by that party's own
     support, so the order is what those voters would put first; the All voters
     edition has no party to rank by and keeps the seat's own order. */
  const policySortKey = isPartyCut ? "second" : "constituency";

  const policyRows = useMemo(() => {
    const rows = FS8_SUBS.map(sub => ({
      sub,
      label: policyWording(sub),
      constituency: policySupport(charts.policies, sub, "constituency"),
      second: isPartyCut
        ? policySupport(charts.policiesParty, sub, "constituency")
        : policySupport(charts.policies, sub, "national"),
    })).filter(r => Number.isFinite(r[policySortKey]));
    return rows.sort((a, b) => b[policySortKey] - a[policySortKey]);
  }, [charts.policies, charts.policiesParty, isPartyCut, policySortKey]);


  // Each column is shaded against its own range, so the two ramps stay
  // readable even when the party column sits systematically higher.
  const policyRange = useMemo(() => {
    const span = (vals) => {
      const v = vals.filter(Number.isFinite);
      if (!v.length) return null;
      const lo = Math.min(...v), hi = Math.max(...v);
      return { lo, hi, size: hi - lo || 1 };
    };
    return {
      constituency: span(policyRows.map(r => r.constituency)),
      second: span(policyRows.map(r => r.second)),
    };
  }, [policyRows]);

  // Rendered on every page from the same props, so the header repeats without
  // the layout being restated.
  const bannerProps = { constName, areaCode, topVI, vi };
  const pageId = (n) => (idPrefix ? `${idPrefix}-p${n}` : undefined);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>

    {/* ═══ PAGE 1 ═══════════════════════════════════════════════ */}
    <Page id={pageId(1)} height={height} rows="auto 1fr auto 1.5fr">

      {/* ── ROW 1: HEADER (auto) ───────────────────────────────── */}
      <InlineHeader {...bannerProps} />

      {/* ── ROW 2: MAPS + VI CHART (1fr) ───────────────────────── */}
      {/* Copied from the political report: past-vote ward map, VI bars in the
          centre, VI ward map. All three are tracking-schema and unfiltered, so
          they show the same seat context on every party cut. */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "6px",
        minHeight: 0,
      }}>

        {/* Left map: past vote by ward */}
        <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
          <WardGroupStaticMapWithBase groupId="past_vote" areaCode={areaCode} />
          <Box sx={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center",
            borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none",
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Past Vote by Ward</Typography>
          </Box>
        </Box>

        {/* Centre: vote intention, constituency vs national */}
        <Box sx={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <BarCompareConstNat data={charts.vi} constName={constName} maxRows={10} />
          </Box>
          <Box sx={{
            flexShrink: 0,
            textAlign: "center", bgcolor: "rgba(255,255,255,0.5)", py: "3px",
            borderTop: `0.5px solid ${BORDER}`,
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>
              {`${constName || "Constituency"} Vote Intention vs National`}
            </Typography>
            {/* The three tiles in this row are tracking-schema and unfiltered, so
                on a party cut they still show the whole seat. Say so, otherwise
                the band at the top implies these bars are the party's voters. */}
            {isPartyCut && (
              <Typography sx={{ fontSize: 8, fontWeight: 500, color: INK2, opacity: 0.75 }}>
                Among all voters
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right map: vote intention by ward */}
        <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
          <WardGroupStaticMapWithBase groupId="vote_intention" areaCode={areaCode} />
          <Box sx={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center",
            borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none",
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Voting Intention by Ward</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── ROW 3: EXTERNAL DATA (auto) ────────────────────────────
          Non-survey constituency data from /api/client_data/. Fixed height so
          the rows below do not shift while it loads, and so an extract that
          does not cover this seat leaves the layout unchanged. */}
      {/* Height is content-driven. It used to be pinned at 278px so the rows
          below could not shift while this loaded, but the block now varies
          legitimately — the food-insecurity row is dropped when the extract has
          no usable figure — and a fixed height overflowed into the charts row
          rather than clipping. The charts row below is fr-sized and absorbs
          whatever is left. */}
      <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>

        <SL color={STACK_TEAL}>Farming in the constituency</SL>
        <Typography sx={{ fontSize: 9, color: INK2, lineHeight: 1.35, mb: "8px", flexShrink: 0 }}>
          {!farmingIntro || essentialUplift === null ? (
            " "
          ) : !farmingIntro.concessive ? (
            <>
              <Box component="span" sx={{ fontWeight: 700, color: INK }}>{constName}</Box>
              {` has ${farmingIntro.land} ${farmingIntro.joiner} ${farmingIntro.jobs} than the country as a whole, and residents are `}
              <Box component="span" sx={{ fontWeight: 700, color: INK }}>
                {Math.abs(essentialUplift).toFixed(0)}%
              </Box>
              {essentialUplift >= 0 ? " more likely" : " less likely"}
              {" to see farming as essential."}
            </>
          ) : (
            <>
              {"Despite "}
              <Box component="span" sx={{ fontWeight: 700, color: INK }}>{constName}</Box>
              {` having ${farmingIntro.land} ${farmingIntro.joiner} ${farmingIntro.jobs} than the country as a whole, a significant proportion of residents (`}
              <Box component="span" sx={{ fontWeight: 700, color: INK }}>
                {Number.isFinite(essential?.allSeat) ? `${essential.allSeat.toFixed(0)}%` : "—"}
              </Box>
              {") view farming as essential."}
            </>
          )}
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", flexShrink: 0 }}>
          <StatTile
            title="% Land farmed"
            value={landFarmed?.seat}
            compare={landFarmed?.national}
            color="#3b7ea1"
          />
          <StatTile
            title="% of local jobs in farming and associated sectors"
            value={farmingEmployed?.seat}
            compare={farmingEmployed?.national}
            color="#7fcdd8"
          />
          {/* The only tile here that follows the cut: on a party edition the
              value is that party's voters and the bracket is the seat as a
              whole, not GB. The party is appended so the title still says so. */}
          {/* Explicit break so the qualifier always starts line two, rather
              than wrapping wherever the column width happens to put it. */}
          <StatTile
            title={<>
              Importance of farming to daily life<br />
              (% saying 'essential'){isPartyCut ? ` — ${vi.label} voters` : ""}
            </>}
            value={essential?.seat}
            compare={essential?.compare}
            color="#2E7D32"
          />
        </Box>

        {/* The three tiles have different denominators, and the middle one is
            the easy one to misread: it is a share of the employed, not of
            residents. */}
        <Typography sx={{ fontSize: 7.5, color: INK3, mt: "5px", lineHeight: 1.3, flexShrink: 0 }}>
          Farming and associated employment is the share of jobs located in
          {" "}{constName} that are in farming, not a share of residents.
        </Typography>

        <Box sx={{ height: "0.5px", bgcolor: BORDER, my: "9px", flexShrink: 0 }} />

        <SL color={STACK_TEAL}>Food security in the constituency</SL>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", flexShrink: 0 }}>
          {/* Hidden at zero. 53 of the 632 seats carry 0 here, and they are
              missing values rather than real ones: they include seats ranked
              among the most deprived in their country, and nothing at all sits
              between 0 and the next lowest figure of 1.08%. Printing "0% at
              high risk of food insecurity" for those seats asserts something
              false and flattering. */}
          {Number(clientData?.area?.pop_at_risk_pct) > 0 && (
            <FoodRow value={formatClientMetric(clientData.area.pop_at_risk_pct, "pct0")}>
              of {possessive(constName)} population live in neighbourhoods at
              high risk of food insecurity (the 30% most at-risk areas in Britain)
            </FoodRow>
          )}
          <FoodRow value={ordinal(clientData?.area?.deprived_rank)}>
            {Number.isFinite(Number(clientData?.area?.country_seat_count))
              ? `most deprived of ${Math.round(clientData.area.country_seat_count)} ${demonymFromCode(areaCode) || ""} constituencies`.replace(/\s+/g, " ")
              : "most deprived constituency ranking unavailable"}
          </FoodRow>
        </Box>
      </Box>

      {/* ── ROW 4: BOTTOM CHARTS (1.5fr) ───────────────────────── */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "7fr 3fr",
        gap: "14px",
        minHeight: 0,
        height: "100%",
      }}>

        {/* LEFT: biggest problems facing farmers */}
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
          <SL color={MCCAIN_RED}>Biggest problems facing farmers</SL>
          <Typography sx={{ fontSize: 8.5, color: INK2, mb: "6px", lineHeight: 1.35, flexShrink: 0 }}>
            Respondents chose up to three - bars are a share of all selections
            made rather than the percentage of people picking each option.
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <BarCompareConstNat
              data={problemsChart}
              constName={constName}
              scaleMax={problemsScaleMax}
              {...partyBarProps}
            />
          </Box>
          {/* Names the two bars, which the chart itself does not label. */}
          <Box sx={{ flexShrink: 0, mt: "6px", display: "flex", alignItems: "center", gap: "10px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box sx={{ width: 14, height: 7, borderRadius: "3.5px", bgcolor: INK2, opacity: 0.85 }} />
              <Typography sx={{ fontSize: 8.5, color: INK2 }}>{constName}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box sx={{
                width: 14,
                height: isPartyCut ? 6 : 4,
                borderRadius: "3px",
                bgcolor: isPartyCut ? vi.color : INK2,
                opacity: isPartyCut ? 0.9 : 0.28,
              }} />
              <Typography sx={{
                fontSize: 8.5,
                color: isPartyCut ? INK : INK2,
                fontWeight: isPartyCut ? 700 : 400,
              }}>
                {secondBarLabel}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* RIGHT: two equal rows. Reserved slot above, weather figure below.
            Halving the column means the donuts drop from 132 to 96 and the
            headline from 11 to 9.5 to keep each block inside its row. */}
        <Box sx={{
          display: "grid",
          gridTemplateRows: "1fr 1fr",
          gap: "12px",
          minHeight: 0,
          height: "100%",
        }}>

          {/* TOP: how fairly farmers are paid (BFG.1).
              KNOWN DATA ISSUE, kept deliberately. This reads 100% in every seat
              and every party cut, because McCain_July26_farmers_paid_mostly_unfair
              and _almost_always_unfair sum to exactly 100.00 in all 632 seats —
              they are a split within the unfair group, not shares of all
              respondents. The five points sum to 180-377 depending on the seat,
              so they are not one distribution and no "% who say unfair" can be
              derived from them as stored. The fix belongs in the modelling, not
              here; the panel stays so it is visible rather than forgotten. */}
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <SL color={MCCAIN_RED}>How fairly farmers are paid</SL>
            <Typography sx={{ fontSize: 9.5, color: INK, mb: "6px", lineHeight: 1.3, flexShrink: 0, fontWeight: 600 }}>
              {Number.isFinite(Number(paidValue))
                ? `${Number(paidValue).toFixed(0)}% of ${isPartyCut ? `potential ${vi.label} voters` : "voters"} in ${constName} think farmers are paid unfairly`
                : "Loading…"}
            </Typography>

            <Box sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <DonutStat
                value={paidValue}
                compareValue={paidCompare}
                color={isPartyCut ? vi.color : MCCAIN_RED}
                size={96}
              />
            </Box>

            <Box sx={{ flexShrink: 0, mt: "5px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "5px", minWidth: 0 }}>
                  <Box sx={{ width: 10, height: 6, borderRadius: "3px", bgcolor: INK3, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 8.5, color: INK2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {paidCompareLabel}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: INK, whiteSpace: "nowrap", flexShrink: 0 }}>
                  {Number.isFinite(Number(paidCompare)) ? `${Number(paidCompare).toFixed(1)}%` : "—"}
                  {paidMoved && (
                    <Box component="span" sx={{ fontSize: 9, fontWeight: 700, color: paidTone, ml: "4px" }}>
                      {paidDelta > 0 ? "+" : "-"}{Math.abs(paidDelta).toFixed(1)}
                    </Box>
                  )}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 7.5, color: INK3, mt: "3px", lineHeight: 1.3 }}>
                "Mostly" and "almost always unfair" combined.
              </Typography>
            </Box>
          </Box>

          {/* BOTTOM: weather impact on farming, single figure */}
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <SL color={MCCAIN_RED}>Weather impact on farming</SL>
            <Typography sx={{ fontSize: 9.5, color: INK, mb: "6px", lineHeight: 1.3, flexShrink: 0, fontWeight: 600 }}>
              {Number.isFinite(Number(climateValue))
                ? `${Number(climateValue).toFixed(0)}% of ${isPartyCut ? `potential ${vi.label} voters` : "voters"} in ${constName} think recent changes in weather have made farming harder`
                : "Loading…"}
            </Typography>

            <Box sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <DonutStat
                value={climateValue}
                compareValue={climateCompare}
                color={isPartyCut ? vi.color : MCCAIN_RED}
                size={96}
                segments={climateSegments}
              />
            </Box>

            {/* Comparison, named and signed the same way as the bar chart's
                bracketed figure so the two panels agree. */}
            <Box sx={{ flexShrink: 0, mt: "5px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "5px", minWidth: 0 }}>
                  <Box sx={{ width: 10, height: 6, borderRadius: "3px", bgcolor: INK3, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 8.5, color: INK2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {climateCompareLabel}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: INK, whiteSpace: "nowrap", flexShrink: 0 }}>
                  {Number.isFinite(Number(climateCompare)) ? `${Number(climateCompare).toFixed(1)}%` : "—"}
                  {climateMoved && (
                    <Box component="span" sx={{ fontSize: 9, fontWeight: 700, color: climateTone, ml: "4px" }}>
                      {climateDelta > 0 ? "▲" : "▼"}{Math.abs(climateDelta).toFixed(1)}
                    </Box>
                  )}
                </Typography>
              </Box>
              {/* Names the two arcs; the total above is their sum. */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "3px 10px", mt: "4px" }}>
                {climateSegments.map(s => (
                  <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: s.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 7.5, color: INK2, whiteSpace: "nowrap" }}>
                      {s.label}
                      {Number.isFinite(s.value) && ` ${s.value.toFixed(0)}%`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

    </Page>

    {/* ═══ PAGE 2 ═══════════════════════════════════════════════
        Same banner, content still to be specified. The tile is real and
        exported, so whatever lands here is clipped to A4 like page 1. */}
    <Page id={pageId(2)} height={height} rows="auto auto auto auto 1fr">

      <InlineHeader {...bannerProps} />

      {/* ── ROW 1: FARMING NOW vs THE NEXT GENERATION (auto) ───── */}
      <Box sx={{ flexShrink: 0 }}>
        <SL color={MCCAIN_RED}>Farming priorities: current/future</SL>
        <Typography sx={{ fontSize: 8.5, color: INK2, mb: "8px", lineHeight: 1.35 }}>
          Each option's share among {audience}. Bracketed figure and tick are
          {isPartyCut ? ` ${constName} as a whole.` : " the national figure."}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
          {efficiencyTiles.length
            ? efficiencyTiles.map(d => (
                <StatTile
                  key={d.key}
                  title={EFFICIENCY_TILES[d.key].label}
                  value={d.constituency}
                  compare={d.compare}
                  color={EFFICIENCY_TILES[d.key].color}
                />
              ))
            : Object.entries(EFFICIENCY_TILES).map(([k, t]) => (
                <StatTile key={k} title={t.label} value={null} compare={null} color={BORDER} />
              ))}
        </Box>
      </Box>

      {/* ── ROW 2: UK FOOD PRODUCTION (auto) ───────────────────── */}
      <Box sx={{ flexShrink: 0, mt: "6px" }}>
        <SL color={MCCAIN_RED}>Food self-sufficiency</SL>
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: "18px",
          border: `1px solid ${BORDER}`,
          // Fixed green rather than the party colour: this is the same
          // "produce more here" direction the green end of the scales above
          // stands for, and it should read that way on every edition.
          borderLeft: `3px solid ${RAG_GREEN}`,
          borderRadius: "8px",
          p: "14px 16px",
          bgcolor: "#fff",
        }}>
          <Typography sx={{
            fontSize: 58,
            fontWeight: 800,
            color: RAG_GREEN,
            lineHeight: 1,
            letterSpacing: "-0.02em",
            flexShrink: 0,
          }}>
            {Number.isFinite(localFoodHigher?.seat) ? `${localFoodHigher.seat.toFixed(0)}%` : "—"}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 650, color: INK, lineHeight: 1.3 }}>
              of {audience} think we should increase the percentage of food
              consumed in the UK that is produced here
            </Typography>
            {/* No signed delta. Support is overwhelming everywhere, so the
                finding is the level, not the gap — a red arrow on a couple of
                points would have argued against the sentence above it. */}
            {Number.isFinite(localFoodHigher?.compare) && (
              <Typography sx={{ fontSize: 9, color: INK2, mt: "5px" }}>
                {localFoodHigher.compareLabel} {localFoodHigher.compare.toFixed(1)}%
              </Typography>
            )}
          </Box>
        </Box>
        {/* FS.2 states the true figure in the question, so this is an informed
            preference rather than a top-of-mind one. */}
        <Typography sx={{ fontSize: 7.5, color: INK3, mt: "4px", lineHeight: 1.3 }}>
          Asked after respondents were told around 65% of food consumed in the UK
          is currently produced here.
        </Typography>
      </Box>

      {/* ── ROW 3: SUPPORTING FARMERS WITH PUBLIC FUNDS (auto) ── */}
      <Box sx={{ flexShrink: 0, mt: "6px" }}>
        <SL color={MCCAIN_RED}>Best approach to supporting farmers with public funds</SL>
        {/* The two figures in each segment were unlabelled: the large one is
            the party, the small one the seat as a whole. Neither is national. */}
        <Typography sx={{ fontSize: 8.5, color: INK2, mb: "8px", lineHeight: 1.35 }}>
          {isPartyCut
            ? `Large figure: those planning to vote ${vi.label} in ${constName}. Small figure: ${constName} as a whole. `
            : `Large figure: ${constName}. Small figure: national. `}
          Figures are shares of all respondents; segment widths are relative to
          one another.
        </Typography>

        <Box sx={{
          display: "flex",
          borderRadius: "8px",
          overflow: "hidden",
          border: `1px solid ${BORDER}`,
          bgcolor: "#f8fafc",
          minHeight: 74,
        }}>
          {(fs10?.segments || FS10_SEGMENTS.map(s => ({ ...s, share: null, width: 100 / 3 }))).map(s => (
            <Box
              key={s.key}
              sx={{
                width: `${s.width}%`,
                bgcolor: fs10 ? s.bg : "#eef2f7",
                p: "10px 8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                minWidth: 0,
              }}
            >
              <Typography sx={{ fontSize: 21, fontWeight: 800, color: fs10 ? s.fg : INK3, lineHeight: 1 }}>
                {fs10 ? `${s.share.toFixed(0)}%` : "—"}
              </Typography>
              <Typography sx={{
                fontSize: 8.5, fontWeight: 600,
                color: fs10 ? s.fg : INK3,
                textAlign: "center", lineHeight: 1.25,
              }}>
                {s.label}
              </Typography>
              {/* The comparison figure: the seat as a whole on a party
                  edition, GB otherwise. Just the number — the caption above
                  names it, and spelling it out wrapped to three lines in the
                  narrower segments. */}
              {Number.isFinite(s.compare) && (
                <Typography sx={{
                  fontSize: 8, fontWeight: 500,
                  color: fs10 ? s.fg : INK3,
                  opacity: 0.8,
                  textAlign: "center", lineHeight: 1.2,
                }}>
                  {s.compare.toFixed(0)}%
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* The three shown do not sum to 100, so name what is missing. */}
        {fs10?.excluded?.length > 0 && (
          <Typography sx={{ fontSize: 7.5, color: INK3, mt: "4px", lineHeight: 1.3 }}>
            Excludes {fs10.excluded.map(e => `${e.value.toFixed(1)}% ${e.label}`).join(", ")}.
          </Typography>
        )}
      </Box>

      {/* ── ROW 4: FS.8 POLICY SUPPORT TABLE (1fr) ─────────────── */}
      <Box sx={{ minHeight: 0, mt: "6px", display: "flex", flexDirection: "column" }}>
        <SL color={MCCAIN_RED}>Policies to improve food security: most-supported</SL>
        <Typography sx={{ fontSize: 9.5, color: INK2, mb: "6px", lineHeight: 1.35, flexShrink: 0 }}>
          % supporting each policy, all respondents. Strongly and somewhat
          support combined, ranked by {isPartyCut ? `${vi.label} voters'` : "constituency"} support.
        </Typography>

        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {/* Column headers */}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: "34px 1fr 68px 68px",
            gap: "2px",
            flexShrink: 0,
            mb: "2px",
          }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: STACK_TEAL, textAlign: "center", alignSelf: "end" }}>
              Rank
            </Typography>
            <Box />
            {/* Named rather than labelled "Constituency" / the bare party, and
                allowed to wrap: seat names run to 40 characters and the column
                is 68px. */}
            <Typography sx={{
              fontSize: 9, fontWeight: 700, color: INK2, textAlign: "center",
              lineHeight: 1.2, alignSelf: "end",
            }}>
              {constName}
            </Typography>
            <Typography sx={{
              fontSize: 9, fontWeight: 700, textAlign: "center",
              lineHeight: 1.2, alignSelf: "end",
              color: isPartyCut ? vi.color : INK2,
            }}>
              {isPartyCut ? `${vi.label} voters` : "National"}
            </Typography>
          </Box>

          {policyRows.map((r, i) => {
            const tC = policyRange.constituency
              ? (r.constituency - policyRange.constituency.lo) / policyRange.constituency.size
              : 0;
            const tS = policyRange.second && Number.isFinite(r.second)
              ? (r.second - policyRange.second.lo) / policyRange.second.size
              : 0;
            const cCell = heatCell(tC, "71, 85, 105");
            // Party column takes the party's own hue; national keeps the slate.
            const sCell = heatCell(tS, isPartyCut ? hexToRgb(vi.color) : "100, 116, 139");

            return (
              <Box
                key={r.sub}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "34px 1fr 68px 68px",
                  gap: "2px",
                  flex: 1,
                  minHeight: 0,
                  mb: "2px",
                }}
              >
                {/* Rank follows the sort: the party's own order on a party
                    edition, the seat's own order otherwise. */}
                <Box sx={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  bgcolor: i % 2 === 0 ? "#f8fafc" : "transparent",
                  borderRadius: "3px",
                }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: STACK_TEAL, lineHeight: 1 }}>
                    {i + 1})
                  </Typography>
                </Box>
                <Box sx={{
                  display: "flex", alignItems: "center",
                  px: "8px",
                  bgcolor: i % 2 === 0 ? "#f8fafc" : "transparent",
                  borderRadius: "3px",
                  minWidth: 0,
                }}>
                  <Typography sx={{ fontSize: 11, color: INK, lineHeight: 1.3 }}>
                    {r.label}
                  </Typography>
                </Box>
                <Box sx={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  bgcolor: cCell.bg, borderRadius: "3px",
                }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: cCell.fg }}>
                    {`${r.constituency.toFixed(0)}%`}
                  </Typography>
                </Box>
                <Box sx={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  bgcolor: Number.isFinite(r.second) ? sCell.bg : "transparent",
                  borderRadius: "3px",
                }}>
                  <Typography sx={{
                    fontSize: 14, fontWeight: 700,
                    color: Number.isFinite(r.second) ? sCell.fg : INK3,
                  }}>
                    {Number.isFinite(r.second) ? `${r.second.toFixed(0)}%` : "—"}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

    </Page>

    </Box>
  );
};

export default McCainReportView;
